import React, { useEffect, useState } from "react";
import { PageHeader, Card, Badge, Button, Spinner, KPICard } from "@/components/ui";
import { endpoints } from "@/lib/api";
import { fmt } from "@/lib/format";
import { TrendingUp, Target, Zap, Clock, ArrowRight, Activity, Download } from "lucide-react";
import {
  BarChart, Bar, LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis,
  CartesianGrid, FunnelChart, Funnel, LabelList, Cell,
} from "recharts";

const STAGE_COLORS = {
  sourcing: "#6b7280",
  diligence: "#60a5fa",
  negotiation: "#c084fc",
  closing: "#C5A059",
  closed: "#34d399",
  exited: "#6ee7b7",
};

const STAGE_LABELS = {
  sourcing: "Sourcing",
  diligence: "Due Diligence",
  negotiation: "Negotiation",
  closing: "Closing",
  closed: "Closed",
  exited: "Exited",
};

export default function OriginPerformance() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await endpoints.originPerformance();
        setData(r.data);
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading || !data) {
    return <div className="p-6 md:p-10"><Spinner /> Loading performance metrics…</div>;
  }

  const funnelData = Object.entries(data.originated_by_stage)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: STAGE_LABELS[k] || k, value: v, fill: STAGE_COLORS[k] }));

  const tsData = data.last_30d_timeseries || [];

  // heat-map color scale
  const maxSectorCount = Math.max(...(data.sector_heatmap || []).map(s => s.count), 1);
  const maxRegionCount = Math.max(...(data.region_heatmap || []).map(s => s.count), 1);
  const heatColor = (ratio) => {
    if (ratio > 0.8) return { bg: "rgba(197,160,89,0.35)", border: "#D4AF37", text: "#fff" };
    if (ratio > 0.5) return { bg: "rgba(197,160,89,0.22)", border: "#C5A059", text: "#fff" };
    if (ratio > 0.25) return { bg: "rgba(197,160,89,0.12)", border: "rgba(197,160,89,0.4)", text: "#e4e4e7" };
    return { bg: "rgba(255,255,255,0.03)", border: "rgba(255,255,255,0.08)", text: "#a1a1aa" };
  };

  return (
    <div className="p-4 md:p-10 max-w-[1500px] mx-auto">
      <PageHeader
        eyebrow="CLARA Origin · Performance"
        title="Origination Dashboard"
        description="Real-time M&A pipeline performance: conversion funnel, sector/region heatmap, time-to-DD, total pipeline value."
        testid="origin-performance-page"
        actions={
          <Button
            data-testid="export-performance-pdf-btn"
            variant="gold"
            onClick={() => window.open(endpoints.originPerformancePdfUrl(), "_blank")}
          >
            <Download size={14} className="mr-2" /> Export PDF
          </Button>
        }
      />

      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4 mb-6 md:mb-8">
        <KPICard testid="kpi-originated" label="Targets Originated" value={data.originated_total} sub="by CLARA Origin AI" accent="#C5A059" />
        <KPICard label="Promoted to DD" value={data.promoted_to_dd} sub={`${data.promoted_to_dd}/${data.originated_total}`} />
        <KPICard label="Closed" value={data.closed_total} sub="executed deals" accent={data.closed_total > 0 ? "#34d399" : "#71717a"} />
        <KPICard label="Conversion Rate" value={`${data.conversion_rate}%`} sub="sourcing → closed" accent="#C5A059" />
        <KPICard label="Avg Time to DD" value={`${data.avg_days_sourcing_to_dd || 0}d`} sub="days sourcing→DD" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8">
        {/* Funnel */}
        <Card className="lg:col-span-2">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="overline">Conversion Funnel</div>
              <div className="font-display text-xl mt-1">Sourcing → Exit</div>
            </div>
            <Badge tone="gold">{data.originated_total} total</Badge>
          </div>
          <div className="space-y-2" data-testid="funnel-chart">
            {funnelData.map((s, i) => {
              const width = funnelData[0] ? (s.value / funnelData[0].value) * 100 : 0;
              return (
                <div key={s.name} className="flex items-center gap-3">
                  <div className="w-28 text-[12px] text-white/70">{s.name}</div>
                  <div className="flex-1 h-9 bg-white/[0.03] rounded-md overflow-hidden relative">
                    <div
                      className="h-full flex items-center px-3 text-[12px] font-semibold transition-all"
                      style={{ width: `${Math.max(width, 4)}%`, background: `linear-gradient(90deg, ${s.fill}80, ${s.fill})`, color: "#0a0a0a" }}
                    >
                      {s.value}
                    </div>
                  </div>
                  <div className="w-16 text-right text-[11px] text-white/50 font-mono">
                    {data.originated_total ? ((s.value / data.originated_total) * 100).toFixed(0) : 0}%
                  </div>
                </div>
              );
            })}
            {funnelData.length === 0 && <div className="text-white/30 text-sm text-center py-6">No CLARA Origin deals yet — run Deep Discovery to populate.</div>}
          </div>
        </Card>

        {/* Pipeline value + share */}
        <Card>
          <div className="overline mb-3">Pipeline</div>
          <div className="space-y-4">
            <div>
              <div className="text-[10px] text-white/50 uppercase tracking-wider mb-1">Total Origin Pipeline €</div>
              <div className="font-display text-3xl text-gold">{fmt.money(data.total_pipeline_value)}</div>
            </div>
            <div>
              <div className="text-[10px] text-white/50 uppercase tracking-wider mb-1">Share of Total Pipeline</div>
              <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                <div className="h-full" style={{ width: `${data.origin_share_of_pipeline}%`, background: "linear-gradient(90deg, #C5A059, #D4AF37)" }} />
              </div>
              <div className="text-[11px] text-white/60 mt-1 font-mono">{data.origin_share_of_pipeline}% originated by CLARA AI</div>
            </div>
            <div className="pt-3 border-t border-white/5">
              <div className="text-[10px] text-white/50 uppercase tracking-wider mb-2">Last 30 days activity</div>
              <div className="h-24">
                <ResponsiveContainer>
                  <LineChart data={tsData}>
                    <Line type="monotone" dataKey="count" stroke="#C5A059" strokeWidth={2} dot={{ r: 2, fill: "#C5A059" }} />
                    <Tooltip
                      contentStyle={{ background: "#18181b", border: "1px solid rgba(197,160,89,0.3)", borderRadius: "6px", fontSize: "11px" }}
                      labelFormatter={(v) => v}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Heatmaps */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card>
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline">Sector Heatmap</div>
              <div className="font-display text-lg mt-1">Top originated sectors</div>
            </div>
            <Zap size={16} className="text-gold" />
          </div>
          <div className="grid grid-cols-2 gap-2" data-testid="sector-heatmap">
            {(data.sector_heatmap || []).map((s) => {
              const c = heatColor(s.count / maxSectorCount);
              return (
                <div key={s.sector} className="p-3 rounded-md border transition" style={{ background: c.bg, borderColor: c.border, color: c.text }}>
                  <div className="text-[11px] font-medium truncate" title={s.sector}>{s.sector}</div>
                  <div className="flex items-baseline justify-between mt-1">
                    <div className="text-lg font-display">{s.count}</div>
                    <div className="text-[10px] opacity-70 font-mono">{fmt.money(s.value)}</div>
                  </div>
                </div>
              );
            })}
            {!data.sector_heatmap?.length && <div className="col-span-2 text-center text-white/30 text-sm py-8">No data yet.</div>}
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline">Region Heatmap</div>
              <div className="font-display text-lg mt-1">By country</div>
            </div>
            <Target size={16} className="text-gold" />
          </div>
          <div className="space-y-2" data-testid="region-heatmap">
            {(data.region_heatmap || []).map((r) => {
              const ratio = r.count / maxRegionCount;
              return (
                <div key={r.region} className="flex items-center gap-3">
                  <div className="w-24 text-[12px] text-white/70 truncate">{r.region}</div>
                  <div className="flex-1 h-7 bg-white/[0.03] rounded overflow-hidden relative">
                    <div
                      className="h-full flex items-center px-2 text-[11px]"
                      style={{
                        width: `${Math.max(ratio * 100, 8)}%`,
                        background: `linear-gradient(90deg, rgba(197,160,89,0.25), rgba(197,160,89,0.55))`,
                        color: "#fff",
                      }}
                    >
                      {r.count} deals
                    </div>
                  </div>
                  <div className="w-20 text-right text-[11px] font-mono text-gold">{fmt.money(r.value)}</div>
                </div>
              );
            })}
            {!data.region_heatmap?.length && <div className="text-center text-white/30 text-sm py-8">No data yet.</div>}
          </div>
        </Card>
      </div>
    </div>
  );
}
